import { Flex, Select, SelectProps, Text } from "@mantine/core";
import { ReactNode, forwardRef, useState } from "react";

import { AutoCompleteInputContainer } from "@/components/atom/inputContainer/autoCompleteInputContainer";
import {
  OperationOption,
  useOperationsAutoComplete,
  useOperationsCode,
} from "@/components/organism/autoComplete/operation/auto-useGetOperationsQuery";
import { IconX } from "@tabler/icons-react";

interface OperationProps extends React.ComponentPropsWithoutRef<"div"> {
  label: string;
  value: string;
}

export interface OperationAutoCompleteProps
  extends Partial<SelectProps>,
    Partial<React.RefAttributes<HTMLInputElement>> {
  value?: string | null;
  onChange?: (operationCode: string | null) => void;
  maxDropdownHeight?: number;
  width?: string;
}

export const OperationAutoComplete = (params: OperationAutoCompleteProps) => {
  const {
    value: operationCode,
    onChange,
    maxDropdownHeight,
    width,
    ...etcParams
  } = params;
  const [focused, setFocused] = useState<boolean>(true);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const { data: options } = useOperationsAutoComplete(focused, searchKeyword);

  const { data: initialOptions } = useOperationsCode(
    !!operationCode,
    operationCode ?? null
  );

  let selectedOperation = initialOptions?.find(
    (operation) => operation.value === operationCode
  );
  const onChangeHandler = (e: string | null) => {
    selectedOperation = options?.find((operation) => operation.value === e);
    onChange && onChange(e);
  };

  const SelectOperation = forwardRef<HTMLDivElement, OperationProps>(
    ({ label: name, value: code, ...others }, ref) => (
      <div ref={ref} {...others}>
        <Flex direction="row" justify="space-between" align="center">
          <Flex align={"center"}>
            <Text>{name}</Text>
            <Text fz="xs">(공정코드: {code})</Text>
          </Flex>
        </Flex>
      </div>
    )
  );

  return (
    <Select
      withinPortal
      onDropdownOpen={() => setFocused(true)}
      onDropdownClose={() => setFocused(false)}
      clearable
      inputContainer={(children: ReactNode) => (
        <AutoCompleteInputContainer selectedValue={operationCode ?? ""}>
          {children}
        </AutoCompleteInputContainer>
      )}
      value={operationCode}
      itemComponent={SelectOperation}
      searchValue={searchKeyword}
      data={[...(options ?? []), ...(initialOptions ?? [])].reduce(
        (unique: OperationProps[], option: OperationProps) => {
          return unique.some((u) => u.value === option.value)
            ? unique
            : [...unique, option];
        },
        []
      )}
      searchable
      maxDropdownHeight={maxDropdownHeight ?? 150}
      onChange={onChangeHandler}
      onSearchChange={setSearchKeyword}
      rightSection={OperationInfo({
        operation: selectedOperation as OperationOption,
        onChange: onChangeHandler,
      })}
      filter={(value, item) =>
        item?.label?.toLowerCase().includes(value.toLowerCase().trim()) ||
        item?.value?.toLowerCase().includes(value.toLowerCase().trim())
      }
      {...etcParams}
    />
  );
};

const OperationInfo = (params: {
  operation?: OperationOption;
  onChange: (operationCode: string | null) => void;
}) => {
  const { operation, onChange } = params;

  const clearHandler: React.MouseEventHandler<SVGSVGElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(null);
  };

  return operation?.value ? (
    <IconX size="1.5rem" onClick={clearHandler} />
  ) : null;
};
